import { Moment } from 'moment';
import { AppointmentStatus } from '@bb/domain/dist/types';
import { Button, Dropdown, Icon, Menu, MenuItem, Tooltip } from 'components';
import React, { useMemo } from 'react';
import { CancelAppointmentsModal } from 'components/PageComponents/ModalComponents';
import { DEFAULT_STATUS_FILTERS, ES_DELAY } from '../constants';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import {
  AgentCalendarMenu,
  AppointmentTypes,
  ListingCalendarMenu,
  ShowingCalendarMenuOption,
  VirtualTableData,
} from '../types';
import { getDateSearchString, getPreferenceSearchString } from '../utils';

const getPrintLink = (
  isPdf: boolean,
  options: {
    menuType: ShowingCalendarMenuOption;
    listingId?: string;
    preferences?: AppointmentStatus[];
    date?: [Moment, Moment];
    searchText?: string;
  },
) => {
  const { listingId, menuType, preferences, date } = options;

  switch (menuType) {
    case AgentCalendarMenu.Listing:
    case AgentCalendarMenu.Buyer:
      return `/appointment/report/download${
        isPdf ? 'PDF' : ''
      }?group=UserDetails&type=${menuType}&sortAsc=true&sortField=time${getPreferenceSearchString(
        preferences,
      )}${getDateSearchString(date)}&searchText=${options.searchText}`;
    case ListingCalendarMenu.InPerson:
    case ListingCalendarMenu.Virtual:
      return `/appointment/report/download${
        isPdf ? 'PDF' : ''
      }?group=ListingDetails&listingId=${listingId}&sortAsc=true&sortField=time${getDateSearchString(
        date,
      )}&searchText=${options.searchText}`;
    default:
      return '/invalidLink';
  }
};

const EllipsisMenu: React.FC = () => {
  const context = useShowingsCalendarContext();

  const {
    statuses,
    selectedMenuOption,
    listing,
    listingId,
    date,
    appointmentList,
    refetchPage,
    searchText,
  } = context;

  const [isVisible, setIsVisible] = React.useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  const selectedAppointments = useMemo(() => {
    if (selectedMenuOption !== ListingCalendarMenu.Virtual) {
      return (
        appointmentList as Array<
          Exclude<AppointmentTypes[number], VirtualTableData[number]>
        >
      ).filter(
        appointment =>
          !appointment.passed &&
          [
            'new',
            'attempted',
            'confirm',
            'time_change',
            'suggested_time_change',
          ].includes(appointment.status!),
      );
    }
  }, [appointmentList, selectedMenuOption]);

  const processedStatuses = !statuses?.length
    ? DEFAULT_STATUS_FILTERS
    : statuses;

  return (
    <>
      <Dropdown
        trigger={['click']}
        open={isVisible}
        onOpenChange={visible => setIsVisible(visible)}
        placement="bottomRight"
        dropdownRender={() => (
          <Menu>
            <MenuItem
              key={'print'}
              data-cy="print-button"
              href={getPrintLink(true, {
                menuType: selectedMenuOption,
                listingId,
                preferences: processedStatuses,
                date,
                searchText,
              })}
              rel="noopener noreferrer"
              target="_blank"
              disabled={!appointmentList?.length}
            >
              Print
            </MenuItem>
            <MenuItem
              key={'export'}
              data-cy="export-button"
              href={getPrintLink(false, {
                menuType: selectedMenuOption,
                listingId,
                preferences: processedStatuses,
                date,
                searchText,
              })}
              rel="noopener noreferrer"
              target="_blank"
              disabled={!appointmentList?.length}
            >
              Export
            </MenuItem>
            {selectedMenuOption !== ListingCalendarMenu.Virtual &&
              selectedMenuOption !== AgentCalendarMenu.Buyer && (
                <MenuItem
                  key={'cancel'}
                  data-cy="export-button"
                  onClick={() => {
                    setIsModalVisible(true);
                    setIsVisible(false);
                  }}
                  disabled={!selectedAppointments?.length}
                >
                  <Tooltip
                    title={
                      !selectedAppointments?.length &&
                      'There are no showings to cancel.'
                    }
                    placement="left"
                  >
                    Cancel Showings
                  </Tooltip>
                </MenuItem>
              )}
          </Menu>
        )}
      >
        <Button
          data-cy="showings-calendar-ellipsis"
          type="text"
          id="ellipsis-button"
        >
          <Icon type="ellipsis" width={25} />
        </Button>
      </Dropdown>
      {isModalVisible && (
        <CancelAppointmentsModal
          address={listing?.line1}
          listingId={listingId}
          appointments={selectedAppointments || []}
          closeModal={() => setIsModalVisible(false)}
          refetchPage={() => refetchPage(ES_DELAY.DEFAULT)}
          modalTitle="Cancel Upcoming Showings"
        />
      )}
    </>
  );
};

export default EllipsisMenu;
